import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'
import { goBack, goToPortfolioDashboard, lightImpactVibrate, postCTA } from '../../../../../utils/appBridge'
import {
  fetchLumpsumAllocationPlans,
  fetchLumpsumIdealPlans,
  fetchLumpsumRebalancePlans
} from '../api/repository'
import type { LumpsumAllocationPlansResponse } from '../api/allocation.types'
import { LumpsumPlansPlanType } from '../api/common.types'
import type { LumpsumIdealPlansResponse } from '../api/ideal.types'
import type { LumpsumRebalancePlansResponse } from '../api/rebalance.types'
import {
  LumpsumPlansContext,
  type LumpsumPlansContextValue,
  type LumpsumPlansQueryState
} from './lumpsumPlansContext'
import { usePlanCategoryExpansionState } from './usePlanCategoryBucketExpansion'
import { AllocationMovementToggle, PlansActionFilter, PlansPlanMode } from '../api/types'
import { LUMPSUM_PLANS_API_RETRY_COUNT } from '../api/queryOptions'

export type { LumpsumPlansQueryState } from './lumpsumPlansContext'
export { useLumpsumPlansContext } from './lumpsumPlansContext'

const LUMPSUM_REBALANCE_PLANS_QUERY_KEY = 'lumpsum-plans-v4-rebalance'
const LUMPSUM_ALLOCATION_PLANS_QUERY_KEY = 'lumpsum-plans-v4-allocation'
const LUMPSUM_PLANS_IDEAL_QUERY_KEY = 'lumpsum-plans-v4-ideal'

type LumpsumPlansRawData = {
  rebalancePlansData: LumpsumRebalancePlansResponse | undefined
  allocationPlansData: LumpsumAllocationPlansResponse | undefined
  idealData: LumpsumIdealPlansResponse | undefined
  rebalancePlansQuery: LumpsumPlansQueryState
  allocationPlansQuery: LumpsumPlansQueryState
  idealPlansQuery: LumpsumPlansQueryState
  refetchAllPlansQueries: () => void
}

function useLumpsumPlansRawData(): LumpsumPlansRawData {
  const rebalancePlansQuery = useQuery<LumpsumRebalancePlansResponse>({
    queryKey: [LUMPSUM_REBALANCE_PLANS_QUERY_KEY],
    queryFn: fetchLumpsumRebalancePlans,
    retry: LUMPSUM_PLANS_API_RETRY_COUNT
  })

  const allocationPlansQuery = useQuery<LumpsumAllocationPlansResponse>({
    queryKey: [LUMPSUM_ALLOCATION_PLANS_QUERY_KEY],
    queryFn: fetchLumpsumAllocationPlans,
    retry: LUMPSUM_PLANS_API_RETRY_COUNT
  })

  const idealPlansQuery = useQuery<LumpsumIdealPlansResponse>({
    queryKey: [LUMPSUM_PLANS_IDEAL_QUERY_KEY],
    queryFn: fetchLumpsumIdealPlans,
    retry: LUMPSUM_PLANS_API_RETRY_COUNT
  })

  function refetchAllPlansQueries() {
    void allocationPlansQuery.refetch()
    void rebalancePlansQuery.refetch()
    void idealPlansQuery.refetch()
  }

  return {
    rebalancePlansData: rebalancePlansQuery.data,
    allocationPlansData: allocationPlansQuery.data,
    idealData: idealPlansQuery.data,
    rebalancePlansQuery: {
      isLoading: rebalancePlansQuery.isLoading,
      isError: rebalancePlansQuery.isError,
      isFetching: rebalancePlansQuery.isFetching,
      refetch: () => {
        void rebalancePlansQuery.refetch()
      }
    },
    allocationPlansQuery: {
      isLoading: allocationPlansQuery.isLoading,
      isError: allocationPlansQuery.isError,
      isFetching: allocationPlansQuery.isFetching,
      refetch: () => {
        void allocationPlansQuery.refetch()
      }
    },
    idealPlansQuery: {
      isLoading: idealPlansQuery.isLoading,
      isError: idealPlansQuery.isError,
      isFetching: idealPlansQuery.isFetching,
      refetch: () => {
        void idealPlansQuery.refetch()
      }
    },
    refetchAllPlansQueries
  }
}

function isPlansListMode(planMode: PlansPlanMode): boolean {
  return planMode === PlansPlanMode.REBALANCE || planMode === PlansPlanMode.ALLOCATION
}

export function isListPlansHeaderLoading(
  planMode: PlansPlanMode,
  hasListPlansData: boolean,
  listPlansQuery: LumpsumPlansQueryState
): boolean {
  if (!isPlansListMode(planMode)) {
    return false
  }
  if (listPlansQuery.isError && listPlansQuery.isFetching) {
    return true
  }
  return listPlansQuery.isLoading && !hasListPlansData
}

export function isListPlansLoading(planMode: PlansPlanMode, listPlansQuery: LumpsumPlansQueryState): boolean {
  return isPlansListMode(planMode) && (listPlansQuery.isLoading || (listPlansQuery.isError && listPlansQuery.isFetching))
}

export function isListPlansFetchFailed(planMode: PlansPlanMode, listPlansQuery: LumpsumPlansQueryState): boolean {
  return isPlansListMode(planMode) && listPlansQuery.isError && !listPlansQuery.isFetching
}

export function isIdealPlansLoading(
  planMode: PlansPlanMode,
  hasIdealData: boolean,
  idealPlansQuery: LumpsumPlansQueryState
): boolean {
  if (planMode !== PlansPlanMode.IDEAL_PORTFOLIO) {
    return false
  }
  if (idealPlansQuery.isError && idealPlansQuery.isFetching) {
    return true
  }
  return idealPlansQuery.isLoading && !hasIdealData
}

export function isIdealPlansFetchFailed(planMode: PlansPlanMode, idealPlansQuery: LumpsumPlansQueryState): boolean {
  return planMode === PlansPlanMode.IDEAL_PORTFOLIO && idealPlansQuery.isError && !idealPlansQuery.isFetching
}

export function LumpsumPlansProvider({ children }: { children: ReactNode }) {
  const [searchParams] = useSearchParams()
  const planTypeQuery = searchParams.get('planType')
  const [planMode, setPlanMode] = useState(
    () => resolvePlanModeFromPlanTypeQuery(planTypeQuery) ?? PlansPlanMode.REBALANCE
  )
  const [actionFiltersByPlanMode, setActionFiltersByPlanMode] = useState<
    Record<PlansPlanMode, PlansActionFilter>
  >(() => createDefaultActionFiltersByPlanMode())
  const [allocationMovementToggle, setAllocationMovementToggle] = useState(
    AllocationMovementToggle.ALLOCATION_MOVEMENT
  )
  const [entrySource, setEntrySource] = useState<string | null>(() => searchParams.get('source'))
  const [hasTimerResumeRefetched, setHasTimerResumeRefetched] = useState(false)

  useEffect(() => {
    const nextPlanMode = resolvePlanModeFromPlanTypeQuery(planTypeQuery)
    if (nextPlanMode == null) {
      return
    }
    setPlanMode(nextPlanMode)
  }, [planTypeQuery])

  useEffect(() => {
    const sourceFromUrl = searchParams.get('source')
    // In-feature navigations may drop query — only update when URL still carries source
    if (sourceFromUrl != null) {
      setEntrySource(sourceFromUrl)
    }
  }, [searchParams])

  const actionFilter = actionFiltersByPlanMode[planMode] ?? PlansActionFilter.ALL

  const plansRawData = useLumpsumPlansRawData()
  const planCategoryExpansion = usePlanCategoryExpansionState()

  const handlePlanModeChange = useCallback(
    (nextPlanMode: PlansPlanMode) => {
      lightImpactVibrate()
      if (nextPlanMode === PlansPlanMode.REBALANCE) {
        const rebalancePlansData = plansRawData.rebalancePlansData
        /*
         * Rebalance tab gate: plan is enabled, available, and subscription-unlocked,
         * but riskProfile is still null. User cannot view the rebalance list yet —
         * fire viewRebalanceRiskProfileCta (native) and stay on the current tab.
         * If any of those flags is false/missing, or riskProfile is set, switch tab normally.
         */
        if (rebalancePlansData != null && rebalancePlansData?.isPlanEnabled
          && rebalancePlansData?.isPlanAvailable && rebalancePlansData?.isPlanSubscriptionEligible
          && rebalancePlansData.riskProfile == null) {
          const viewRebalanceRiskProfileCta = rebalancePlansData.viewRebalanceRiskProfileCta
          if (viewRebalanceRiskProfileCta != null) {
            postCTA(JSON.stringify(viewRebalanceRiskProfileCta))
          }
          return
        }
      }
      setPlanMode(nextPlanMode)
    },
    [plansRawData.rebalancePlansData]
  )

  const handleActionFilterChange = useCallback(
    (nextActionFilter: PlansActionFilter) => {
      setActionFiltersByPlanMode((previousFiltersByPlanMode) => ({
        ...previousFiltersByPlanMode,
        [planMode]: nextActionFilter
      }))
    },
    [planMode]
  )

  const handleAllocationMovementToggleChange = useCallback(
    (nextAllocationMovementToggle: AllocationMovementToggle) => {
      setAllocationMovementToggle(nextAllocationMovementToggle)
    },
    []
  )

  const markTimerResumeRefetch = useCallback(() => {
    setHasTimerResumeRefetched(true)
  }, [])

  const leavePlansList = useCallback(() => {
    if (entrySource === 'PORTFOLIO_DASHBOARD' && hasTimerResumeRefetched) {
      goToPortfolioDashboard()
      return
    }
    goBack()
  }, [entrySource, hasTimerResumeRefetched])

  const contextValue = useMemo(
    (): LumpsumPlansContextValue => ({
      planMode,
      handlePlanModeChange,
      actionFilter,
      actionFiltersByPlanMode,
      handleActionFilterChange,
      allocationMovementToggle,
      handleAllocationMovementToggleChange,
      ...plansRawData,
      ...planCategoryExpansion,
      markTimerResumeRefetch,
      leavePlansList
    }),
    [
      planMode,
      handlePlanModeChange,
      actionFilter,
      actionFiltersByPlanMode,
      handleActionFilterChange,
      allocationMovementToggle,
      handleAllocationMovementToggleChange,
      plansRawData,
      planCategoryExpansion,
      markTimerResumeRefetch,
      leavePlansList
    ]
  )

  return <LumpsumPlansContext.Provider value={contextValue}>{children}</LumpsumPlansContext.Provider>
}

function createDefaultActionFiltersByPlanMode(): Record<PlansPlanMode, PlansActionFilter> {
  return {
    [PlansPlanMode.REBALANCE]: PlansActionFilter.ALL,
    [PlansPlanMode.ALLOCATION]: PlansActionFilter.ALL,
    [PlansPlanMode.IDEAL_PORTFOLIO]: PlansActionFilter.ALL
  }
}

function resolvePlanModeFromPlanTypeQuery(planTypeQuery: string | null): PlansPlanMode | null {
  if (planTypeQuery === LumpsumPlansPlanType.REBALANCE) {
    return PlansPlanMode.REBALANCE
  }
  if (planTypeQuery === LumpsumPlansPlanType.ALLOCATION) {
    return PlansPlanMode.ALLOCATION
  }
  if (planTypeQuery === LumpsumPlansPlanType.IDEAL) {
    return PlansPlanMode.IDEAL_PORTFOLIO
  }
  return null
}
